import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Switch,
  Typography,
  Upload,
  Skeleton,
  Divider,
} from "antd";
import React, { useEffect, useState, lazy, Suspense, memo } from "react";
import {
  FaPlus,
  FaTrash,
  FaUpload,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { Editor } from "primereact/editor";
import { apiHandle } from "../../config/apiHandle";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllStoresAsync } from "../../services/StoreManagementServices";
import {
  addProductAsync,
  myProductsDetailsAsync,
  updateProductAsync,
} from "../../services/ProductManagementServices";
import { asyncStatus } from "../../utils/asyncStatus";
import {
  setAddProductIdleStatus,
  setUpdateProductIdleStatus,
} from "../../store/slice/ProductManagementSlice";
import { getAllTypes, getAllVariants } from "../../services/variantService";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// Memoized Variant Component for better performance
const VariantItem = memo(
  ({
    variant,
    index,
    variantTypes,
    updateVariant,
    removeVariant,
    isExpanded,
    toggleExpand,
  }) => {
    if (!variant) return null;

    return (
      <div className="p-4 bg-white shadow-md rounded-lg mb-4">
        <div className="flex justify-between items-center mb-2">
          <div className="font-semibold">Variant #{index + 1}</div>
          <div className="flex gap-2">
            <Button
              type="text"
              icon={isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              onClick={() => toggleExpand(index)}
            />
            <Button
              type="danger"
              icon={<FaTrash />}
              onClick={() => removeVariant(index)}
            >
              Remove
            </Button>
          </div>
        </div>

        {isExpanded && (
          <>
            {variantTypes?.map((variantType) => (
              <Form.Item label={variantType.name} key={variantType._id}>
                <Select
                  placeholder={`Select ${variantType.name}`}
                  value={variant[variantType.name]}
                  onChange={(value) =>
                    updateVariant(index, variantType.name, value)
                  }
                  style={{ width: "100%" }}
                >
                  {variantType.values.map((value) => (
                    <Option key={value} value={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ))}

            <Form.Item label="sku" required>
              <Input
                type="text"
                placeholder="Enter sku"
                value={variant.sku}
                onChange={(e) => updateVariant(index, "sku", e.target.value)}
              />
            </Form.Item>
            <Form.Item label="Weight" required>
              <Input
                type="text"
                placeholder="Enter Weight"
                value={variant.weight}
                onChange={(e) => updateVariant(index, "weight", e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Price" required>
              <Input
                type="number"
                placeholder="Enter price"
                value={variant.price}
                onChange={(e) => updateVariant(index, "price", e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Discount Price">
              <Input
                type="text"
                placeholder="Enter discount price"
                value={variant.discount}
                onChange={(e) =>
                  updateVariant(index, "discount", e.target.value)
                }
              />
            </Form.Item>

            <Form.Item label="Stock Quantity">
              <Input
                type="number"
                placeholder="Enter Quantity"
                value={variant.stock_quantity}
                onChange={(e) =>
                  updateVariant(index, "stock_quantity", e.target.value)
                }
              />
            </Form.Item>
          </>
        )}
      </div>
    );
  }
);

// Memoized FAQ Item
const FaqItem = memo(({ faq, index, removeFaq }) => (
  <div className="mb-4 p-4 bg-white shadow-md rounded-lg">
    <Form.Item
      label="Question"
      name={["faqs", index, "question"]}
      rules={[{ required: true, message: "Question is required" }]}
    >
      <Input placeholder="Enter question" />
    </Form.Item>
    <Form.Item
      label="Answer"
      name={["faqs", index, "answer"]}
      rules={[{ required: true, message: "Answer is required" }]}
    >
      <TextArea
        placeholder="Enter answer"
        rows={4}
        style={{ resize: "none" }}
      />
    </Form.Item>
    <Button type="danger" icon={<FaTrash />} onClick={() => removeFaq(index)}>
      Remove
    </Button>
  </div>
));

// Memoized Image Gallery
const ImageGallery = memo(({ images, uploadingImages, handleRemoveImage }) => (
  <div className="flex flex-wrap mt-2">
    {images?.map((image, index) => (
      <div key={index} className="relative m-1">
        <img
          src={image}
          alt={`Uploaded ${index}`}
          className="w-28 h-28 object-cover rounded-md border"
        />
        <Button
          type="text"
          className="absolute top-0 right-0 text-red-500 rounded-full shadow p-1"
          icon={<FaTrash />}
          onClick={() => handleRemoveImage(image)}
        />
      </div>
    ))}
    {uploadingImages?.map((id) => (
      <div
        key={id}
        className="w-28 h-28 flex items-center justify-center bg-gray-300 rounded-md m-1"
      >
        <Spin />
      </div>
    ))}
  </div>
));

const AddProduct = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [duplicateError, setDuplicateError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isStoreMode, setIsStoreMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedVariants, setExpandedVariants] = useState([]);

  const [seoData, setSeoData] = useState({
    meta_title: "",
    meta_description: "",
    meta_url: "",
  });

  const [product, setProduct] = useState({
    name: "",
    name_fr: "", // Added French name field
    description: "",
    description_fr: "", // Added French description field
    category: [],
    images: [],
    sku: "",
    gtin_number: "",
    availability: "In-Stock",
    status: "Active",
    faqs: [],
    stores: [],
    shipping_weight: "",
    country_origin: "",
    hs_number: "",
    variants: [],
    is_popular: false,
    best_selling: false,
  });

  const [uploadingImages, setUploadingImages] = useState([]);
  const [faqs, setFaqs] = useState([]);

  const {
    add_product_status,
    get_my_product_details_data,
    update_product_status,
  } = useSelector((s) => s.productManagement);

  const { get_all_stores_data } = useSelector((s) => s.storeManagement);
  const { get_variants_data, get_types_data } = useSelector((s) => s.variants);
  const { user } = useSelector((s) => s.userAuth);

  // Toggle variant expansion
  const toggleVariantExpand = (index) => {
    setExpandedVariants((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  console.log(get_variants_data, "get_variants_data get_variants_data");

  // Add a new variant and expand it
  const addVariant = () => {
    const newVariant = {};
    console.log(get_variants_data, "get_variants_data");
    get_variants_data.forEach((variantType) => {
      newVariant[variantType.name] = "";
    });
    newVariant.price = "";
    newVariant.discount = "";
    newVariant.weight = "";
    newVariant.stock_quantity = "";
    newVariant.sku = "";

    const updatedVariants = [...product.variants, newVariant];

    setProduct((prev) => ({
      ...prev,
      variants: updatedVariants,
    }));

    // Automatically expand the newly added variant
    setExpandedVariants((prev) => [...prev, updatedVariants.length - 1]);

    isVariantUnique(updatedVariants);
  };

  useEffect(() => {
    // Set loading state
    setIsLoading(true);

    // Load initial data
    Promise.all([
      dispatch(getAllStoresAsync()),
      dispatch(myProductsDetailsAsync()),
      dispatch(getAllVariants()),
      dispatch(getAllTypes()),
    ]).finally(() => {
      setTimeout(() => setIsLoading(false), 500);
    });
  }, [dispatch]);

  useEffect(() => {
    // This will handle the initial form setup for new products
    if (!location.state) {
      // Only run for new products
      // Initialize with empty stores array explicitly
      form.setFieldsValue({ stores: [] });

      // Make sure product state is properly initialized
      setProduct((prev) => ({
        ...prev,
        stores: [],
        all_store: false,
      }));

      // Ensure the checkbox is unchecked initially
      setIsStoreMode(false);
    }
  }, [form]);

  useEffect(() => {
    if (location.state) {
      setIsEditMode(true);
      const editData = location.state;

      // Process variants for edit mode
      const processedVariants = editData.variants.map((variant) => ({
        ...variant,
        // Ensure all variant types are present
        ...get_variants_data?.reduce((acc, vt) => {
          acc[vt.name] = variant[vt.name] || "";
          return acc;
        }, {}),
      }));

      setIsStoreMode(editData.all_store || true);

      // Process stores selection
      const allStoreIds =
        get_all_stores_data?.stores?.map((store) => store?._id) || [];
      const shouldSelectAll =
        editData.all_store || editData.stores?.length === allStoreIds.length;

      setIsStoreMode(shouldSelectAll);

      // Prepare stores data
      const selectedStores = editData.all_store
        ? allStoreIds
        : editData.stores?.map((s) => s._id) || [];

      // Process form data
      const initialFormValues = {
        ...editData,
        category: editData.category?.map((c) => c._id),
        stores: selectedStores,
        variants: processedVariants,
        meta_title: editData?.meta_title || "",
        meta_description: editData?.meta_description || "",
        meta_url: editData?.meta_url || "",
        name_fr: editData?.name_fr || "", // Include French name
        description_fr: editData?.description_fr || "", // Include French description
      };

      // Set form values
      form.setFieldsValue(initialFormValues);
      setFaqs(editData.faqs || []);
      form.setFieldsValue({ ...initialFormValues, faqs: editData.faqs });

      // Update local state
      setProduct((prev) => ({
        ...prev,
        ...initialFormValues,
        variants: processedVariants,
        images: editData.images || [],
      }));
    }
  }, [location.state, get_variants_data, form, get_all_stores_data]);

  const addProductLoader =
    add_product_status === asyncStatus.LOADING ||
    update_product_status === asyncStatus.LOADING;

  const handleImageUpload = async (file) => {
    setUploadingImages((prev) => [...prev, file.uid]);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await apiHandle.post("s3-cloud-storage", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response?.data?.files?.url) {
        setProduct((prev) => {
          const updatedImages = [...prev.images, response?.data?.files?.url];
          form.setFieldsValue({ images: updatedImages });
          form.validateFields(["images"]);
          return { ...prev, images: updatedImages };
        });
      }
    } catch (error) {
      console.error("Image upload failed", error);
    } finally {
      setUploadingImages((prev) => prev.filter((id) => id !== file.uid));
    }
  };

  const addFaq = () => {
    setFaqs([...faqs, { question: "", answer: "" }]);
  };

  const removeVariant = (index) => {
    const updatedVariants = product.variants.filter((_, i) => i !== index);
    setProduct((prev) => ({
      ...prev,
      variants: updatedVariants,
    }));

    // Update expanded variants array
    setExpandedVariants((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );

    isVariantUnique(updatedVariants);
  };

  const updateVariant = (index, field, value) => {
    const updatedVariants = [...product.variants];
    updatedVariants[index][field] = value;
    isVariantUnique(updatedVariants);

    setProduct((prev) => {
      return { ...prev, variants: updatedVariants };
    });
  };

  const isVariantUnique = (updatedVariants) => {
    const seenVariants = new Set();
    for (let variant of updatedVariants) {
      const key = get_variants_data
        .map((variantType) => variant[variantType.name])
        .join("-");
      if (seenVariants.has(key)) {
        setDuplicateError(
          "Each variant must have a unique combination of variant values."
        );
        return false;
      }
      seenVariants.add(key);
    }
    setDuplicateError(null);
    return true;
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const handleRemoveImage = (imageUrl) => {
    setProduct((prev) => ({
      ...prev,
      images: prev.images.filter((image) => image !== imageUrl),
    }));
  };

  const handleSubmit = (values) => {
    if (duplicateError) {
      return;
    }
    const payload = {
      ...values,
      variants: product.variants,
      images: product.images,
      all_store: isStoreMode,
      availability: product.availability,
      status: product.status,
      meta_title: values.meta_title || "",
      meta_description: values.meta_description || "",
      meta_url: values.meta_url || "",
      name_fr: values.name_fr || "", // Include French name in payload
      description_fr: product.description_fr || "", // Include French description in payload
    };
    try {
      if (isEditMode) {
        dispatch(
          updateProductAsync({ id: location.state._id, post_data: payload })
        );
      } else {
        dispatch(addProductAsync(payload));
      }
    } catch (error) {
      console.error("Submission failed", error);
    }
  };

  const handleDescriptionChange = (content) => {
    setProduct({ ...product, description: content });
    form.setFieldsValue({ description: content });
  };

  // New handler for French description
  const handleDescriptionFrChange = (content) => {
    setProduct({ ...product, description_fr: content });
    form.setFieldsValue({ description_fr: content });
  };

  useEffect(() => {
    if (add_product_status === asyncStatus.SUCCEEDED) {
      navigate(-1);
      dispatch(setAddProductIdleStatus());
    }
  }, [add_product_status]);

  useEffect(() => {
    if (update_product_status === asyncStatus.SUCCEEDED) {
      navigate(-1);
      dispatch(setUpdateProductIdleStatus());
    }
  }, [update_product_status]);

  // Check if we should show the starting price field
  const shouldShowStartingPrice = product.variants.length === 0;

  return (
    <div className="flex justify-center">
      <Card className="max-w-4xl w-full shadow-lg rounded-xl overflow-hidden p-2">
        <Row gutter={24}>
          <Col xs={24} md={24} className="p-2">
            <div className="p-4 rounded-lg">
              <div className="flex items-center justify-center w-full mb-4">
                <Title level={4}>
                  {isEditMode ? "Edit Product" : "Add Product"}
                </Title>
              </div>

              {isLoading ? (
                <div className="p-6 bg-gray-100 rounded-lg">
                  <Skeleton active paragraph={{ rows: 12 }} />
                </div>
              ) : (
                <Form
                  form={form}
                  layout="vertical"
                  className="p-6 bg-gray-100 rounded-lg"
                  onFinish={handleSubmit}
                >
                  {/* Basic Product Information Section */}
                  <Collapse
                    defaultActiveKey={["basicInfo"]}
                    className="mb-4"
                    bordered={false}
                  >
                    <Panel
                      header={<Title level={5}>Basic Information</Title>}
                      key="basicInfo"
                    >
                      <Form.Item
                        label="Product Name (English)"
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: "Product name is required",
                          },
                        ]}
                      >
                        <Input placeholder="Enter product name in English" />
                      </Form.Item>

                      {/* Added French Name Field */}
                      <Form.Item
                        label="Product Name (French)"
                        name="name_fr"
                      >
                        <Input placeholder="Enter product name in French" />
                      </Form.Item>

                      {user.role !== "Vendor" && (
                        <Form.Item
                          label="Stores"
                          name="stores"
                          rules={[
                            {
                              required: user.role !== "Vendor",
                              message: "Please select at least one store",
                            },
                          ]}
                        >
                          <div>
                            <Checkbox
                              checked={isStoreMode}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setIsStoreMode(checked);

                                if (checked) {
                                  const allStoreIds =
                                    get_all_stores_data?.stores?.map(
                                      (store) => store?._id
                                    ) || [];
                                  setProduct((prev) => ({
                                    ...prev,
                                    stores: allStoreIds,
                                  }));
                                  form.setFieldsValue({ stores: allStoreIds });
                                } else {
                                  setProduct((prev) => ({
                                    ...prev,
                                    stores: [],
                                  }));
                                  form.setFieldsValue({ stores: [] });
                                }
                              }}
                            >
                              {/* Select All Stores */}
                              National Product
                            </Checkbox>
                          </div>
                          <Select
                            mode="multiple"
                            placeholder="Select stores"
                            value={product.stores || []}
                            allowClear={true}
                            optionFilterProp="children"
                            onChange={(value) => {
                              const allStoreIds =
                                get_all_stores_data?.stores?.map(
                                  (store) => store?._id
                                ) || [];
                              const isAllSelected =
                                value.length === allStoreIds.length;

                              setProduct((prev) => ({
                                ...prev,
                                stores: value,
                                all_store: isAllSelected,
                              }));
                              setIsStoreMode(isAllSelected);
                              form.setFieldsValue({ stores: value });
                            }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                          >
                            {get_all_stores_data?.stores?.map((store) => (
                              <Option key={store?._id} value={store?._id}>
                                {store?.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}

                      <div className="mt-4">
                        <Form.Item label="Description (English)" name="description">
                          <Editor
                            style={{ height: "200px" }}
                            value={product?.description}
                            onTextChange={(e) =>
                              handleDescriptionChange(e.htmlValue)
                            }
                          />
                        </Form.Item>
                      </div>

                      {/* Added French Description Field */}
                      <div className="mt-4">
                        <Form.Item label="Description (French)" name="description_fr">
                          <Editor
                            style={{ height: "200px" }}
                            value={product?.description_fr}
                            onTextChange={(e) =>
                              handleDescriptionFrChange(e.htmlValue)
                            }
                          />
                        </Form.Item>
                      </div>

                      <Form.Item
                        label="Category"
                        name="category"
                        rules={[
                          {
                            required: true,
                            message: "Please select a category",
                          },
                        ]}
                      >
                        <Select mode="multiple" placeholder="Select category">
                          {get_my_product_details_data?.categories
                            ?.filter((cat) => cat?.status === "Active")
                            ?.map((category) => {
                              return (
                                <Option
                                  key={category?._id}
                                  value={category?._id}
                                >
                                  {category?.name}
                                </Option>
                              );
                            })}
                        </Select>
                      </Form.Item>

                      {/* Starting Price field - only visible when there are no variants */}
                      {shouldShowStartingPrice && (
                        <Form.Item
                          label="Starting Price"
                          name="starting_price"
                          rules={[
                            {
                              message: "Price must be a number",
                              pattern: /^-?\d*(\.\d+)?$/,
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Enter starting price"
                            addonBefore="$"
                            min={0}
                          />
                        </Form.Item>
                      )}
                    </Panel>
                  </Collapse>

                  {/* Product Variants Section */}
                  <Collapse
                    defaultActiveKey={["variants"]}
                    className="mb-4"
                    bordered={false}
                  >
                    <Panel
                      header={
                        <div className="flex items-center justify-between w-full">
                          <Title level={5}>Product Variants</Title>
                          <Button
                            type="primary"
                            icon={<FaPlus />}
                            onClick={(e) => {
                              e.stopPropagation();
                              addVariant();
                            }}
                            size="small"
                          >
                            Add Variant
                          </Button>
                        </div>
                      }
                      key="variants"
                    >
                      {product?.variants?.map((variant, index) => (
                        <VariantItem
                          key={index}
                          variant={variant}
                          index={index}
                          variantTypes={get_variants_data}
                          updateVariant={updateVariant}
                          removeVariant={removeVariant}
                          isExpanded={expandedVariants.includes(index)}
                          toggleExpand={toggleVariantExpand}
                        />
                      ))}

                      {duplicateError && (
                        <div className="my-4">
                          <Alert
                            message="Error"
                            description={duplicateError}
                            type="error"
                            showIcon
                          />
                        </div>
                      )}

                      {product.variants.length === 0 && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <Text type="secondary">
                            No variants added yet. Click 'Add Variant' to create
                            one.
                          </Text>
                        </div>
                      )}
                    </Panel>
                  </Collapse>

                  {/* Additional Information Section */}
                  <Collapse className="mb-4" bordered={false}>
                    <Panel
                      header={<Title level={5}>Additional Information</Title>}
                      key="additionalInfo"
                    >
                      <Form.Item label="GTIN Number" name="gtin_number">
                        <Input placeholder="Enter GTIN Number" />
                      </Form.Item>
                      <Form.Item
                        label={
                          <>
                            Shipping Weight &nbsp; <span> (lb)</span>
                          </>
                        }
                        name="shipping_weight"
                      >
                        <Input placeholder="Enter Shipping Weight (lb)" />
                      </Form.Item>
                      <Form.Item
                        label="Country/Region of Origin"
                        name="country_origin"
                      >
                        <Input placeholder="Enter Country/Region of Origin" />
                      </Form.Item>
                      <Form.Item label="HS Number" name="hs_number">
                        <Input placeholder="Enter HS Code" />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Popular Product"
                            name="is_popular"
                            valuePropName="checked"
                            initialValue={product.is_popular || false}
                          >
                            <Switch
                              checkedChildren="Yes"
                              unCheckedChildren="No"
                              onChange={(checked) =>
                                setProduct((prev) => ({
                                  ...prev,
                                  is_popular: checked,
                                }))
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Best Selling Product"
                            name="best_selling"
                            valuePropName="checked"
                            initialValue={product.best_selling || false}
                          >
                            <Switch
                              checkedChildren="Yes"
                              unCheckedChildren="No"
                              onChange={(checked) =>
                                setProduct((prev) => ({
                                  ...prev,
                                  best_selling: checked,
                                }))
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item label="Availability" name="availability">
                            <Select
                              defaultValue="In-Stock"
                              onChange={(value) =>
                                setProduct((prev) => ({
                                  ...prev,
                                  availability: value,
                                }))
                              }
                            >
                              <Option value="In-Stock">In Stock</Option>
                              <Option value="Out of Stock">Out of Stock</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="Status" name="status">
                            <Select
                              defaultValue={product.status}
                              onChange={(value) =>
                                setProduct((prev) => ({
                                  ...prev,
                                  status: value,
                                }))
                              }
                            >
                              <Option value="Active">Active</Option>
                              <Option value="In-Active">In Active</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Panel>
                  </Collapse>

                  {/* Images Section */}
                  <Collapse
                    defaultActiveKey={["images"]}
                    className="mb-4"
                    bordered={false}
                  >
                    <Panel
                      header={<Title level={5}>Product Images</Title>}
                      key="images"
                    >
                      <Form.Item
                        label="Upload Images"
                        name="images"
                        rules={[
                          {
                            required: true,
                            message: "Please add at least one image",
                          },
                        ]}
                      >
                        <Upload
                          multiple
                          listType="picture-card"
                          showUploadList={false}
                          customRequest={({ file, onSuccess }) => {
                            handleImageUpload(file);
                            setTimeout(() => onSuccess("ok"), 0);
                          }}
                        >
                          <Button
                            style={{
                              boxShadow: "none",
                              backgroundColor: "transparent",
                              border: "none",
                            }}
                            icon={<FaUpload />}
                          >
                            Upload
                          </Button>
                        </Upload>
                        <ImageGallery
                          images={product?.images}
                          uploadingImages={uploadingImages}
                          handleRemoveImage={handleRemoveImage}
                        />
                      </Form.Item>
                    </Panel>
                  </Collapse>

                  {/* FAQs Section */}
                  <Collapse className="mb-4" bordered={false}>
                    <Panel
                      header={
                        <div className="flex items-center justify-between w-full">
                          <Title level={5}>Product FAQs</Title>
                          <Button
                            type="primary"
                            icon={<FaPlus />}
                            onClick={(e) => {
                              e.stopPropagation();
                              addFaq();
                            }}
                            size="small"
                          >
                            Add FAQ
                          </Button>
                        </div>
                      }
                      key="faqs"
                    >
                      {faqs.map((faq, index) => (
                        <FaqItem
                          key={index}
                          faq={faq}
                          index={index}
                          removeFaq={removeFaq}
                        />
                      ))}

                      {faqs.length === 0 && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <Text type="secondary">
                            No FAQs added yet. Click 'Add FAQ' to create one.
                          </Text>
                        </div>
                      )}
                    </Panel>
                  </Collapse>

                  {user.role === "Admin" && (
                    <Collapse
                      className="mb-4"
                      bordered={false}
                      defaultActiveKey={["seo"]}
                    >
                      <Panel
                        header={
                          <Title level={5}>Product Meta Information</Title>
                        }
                        key="seo"
                      >
                        <Form.Item label="Meta Title" name="meta_title">
                          <Input placeholder="Enter meta title" />
                        </Form.Item>
                        <Form.Item
                          label="Meta Description"
                          name="meta_description"
                        >
                          <TextArea
                            rows={3}
                            placeholder="Enter meta description"
                            style={{ resize: "none" }}
                          />
                        </Form.Item>
                        <Form.Item label="Meta URL" name="meta_url">
                          <Input placeholder="Enter custom URL handle" />
                        </Form.Item>
                      </Panel>
                    </Collapse>
                  )}

                  <Divider />

                  <Button
                    type="primary"
                    htmlType="submit"
                    className="w-full"
                    loading={addProductLoader}
                    disabled={addProductLoader}
                    size="large"
                  >
                    {isEditMode ? "Update Product" : "Submit"}
                  </Button>
                </Form>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default AddProduct;